/**
 * Flow map work the user left behind without answering a conflict.
 *
 * Autosave stops the moment the server answers 409: the map the user is
 * holding and the map the server holds have both moved, and only the user can
 * say which one wins. Until they pick, nothing may be sent - sending would
 * overwrite the other person's save on their behalf.
 *
 * That leaves one hole. A click to another CRM screen unmounts the panel, and
 * with it went the only copy of the unsaved document: the user came back to the
 * server's version with no sign that their own work had ever existed. This
 * module is the small shelf that hole needed. The unanswered work waits here
 * while the user is elsewhere in the app, and the same map picks it up again
 * when it opens, conflict banner and all, so the choice is still theirs.
 *
 * In memory on purpose, not in storage. A reload is already guarded by the
 * browser's own "leave site?" prompt, and a document that survived a reload
 * would raise a question this shelf does not have to answer: whether work from
 * a window the user closed hours ago should still come back.
 */
import type { FlowMapDoc } from "@wa/widgets/flow-map/model";
import type { FlowMapView } from "../../../../lib/flowMap";
import type { SaveState } from "./saveState";

/** One map's unanswered work: what the user was holding, and where the save had got to. */
export interface ParkedWork {
  /** The local document, including every edit the server never took. */
  doc: FlowMapDoc;
  /** The server's last word on this map, kept for its metadata. */
  view: FlowMapView | null;
  /** The save machine mid-conflict, so the banner and the two choices come back intact. */
  save: SaveState;
}

/**
 * How many maps may hold work at once. A person answers a conflict or abandons
 * it; a queue this deep means neither happened, and the oldest entry is the one
 * least likely to still be wanted.
 */
const MAX_PARKED = 5;

const shelf = new Map<string, ParkedWork>();

function key(owner: string, ownerId: string): string {
  return `${owner}:${ownerId}`;
}

/** Leave this map's unanswered work for the user's return. Replaces anything already held for it. */
export function parkWork(owner: string, ownerId: string, work: ParkedWork): void {
  const k = key(owner, ownerId);
  // Delete first so a replacement counts as the newest, not as its old position:
  // Map keeps insertion order, which is what makes the oldest one findable.
  shelf.delete(k);
  shelf.set(k, work);
  while (shelf.size > MAX_PARKED) {
    const oldest = shelf.keys().next();
    if (oldest.done) break;
    shelf.delete(oldest.value);
  }
}

/**
 * Take back this map's work, if any is waiting.
 *
 * Taking removes it: the caller now owns the only copy, and parks it again
 * itself if the user leaves a second time without answering.
 */
export function takeParkedWork(owner: string, ownerId: string): ParkedWork | null {
  const k = key(owner, ownerId);
  const work = shelf.get(k);
  if (!work) return null;
  shelf.delete(k);
  return work;
}

/** How many maps are holding work. For tests and for reading the shelf's size. */
export function parkedCount(): number {
  return shelf.size;
}

/** Forget everything. For tests, which must not inherit each other's shelf. */
export function clearParkedWork(): void {
  shelf.clear();
}
